/** Delay before each automatic retry round; its length is the max number of retries per file. */
export const AUTO_RETRY_DELAYS_MS = [1_000, 3_000, 5_000];

/** Matches Uppy's default for the S3 plugin, so zip and folder uploads load S3 alike. */
export const UPLOAD_CONCURRENCY = 6;

/** A presigned PUT that S3 answered with a non-2xx status. */
export class UploadHttpError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Upload failed with status ${status}`);
    this.name = "UploadHttpError";
    this.status = status;
  }
}

/**
 * Whether a failed PUT is worth retrying: network errors (status 0), timeouts,
 * throttling and 5xx. Other 4xx (bad signature, expired URL) won't fix themselves.
 * Reads the status off Uppy's errors (`source.status`) or `UploadHttpError`.
 */
export const isRetryableUploadError = (error: unknown): boolean => {
  const { source, status: ownStatus } = (error ?? {}) as {
    source?: { status?: number };
    status?: number;
  };
  const status = source?.status ?? ownStatus;
  return (
    status == null ||
    status === 0 ||
    status === 408 ||
    status === 429 ||
    status >= 500
  );
};

export const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** PUTs `blob` to a presigned URL. The browser sets Content-Type from `blob.type`, as it does for a `File`. */
const putBlob = async (url: string, blob: Blob): Promise<void> => {
  const response = await fetch(url, { method: "PUT", body: blob });
  if (!response.ok) {
    throw new UploadHttpError(response.status);
  }
};

/** PUTs `blob`, retrying retryable failures with backoff; throws the last error once retries run out. */
export const putBlobWithRetry = async (
  url: string,
  blob: Blob,
  retryDelaysMs: readonly number[] = AUTO_RETRY_DELAYS_MS,
): Promise<void> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await putBlob(url, blob);
      return;
    } catch (error) {
      if (attempt >= retryDelaysMs.length || !isRetryableUploadError(error)) {
        throw error;
      }
      await wait(retryDelaysMs[attempt]);
    }
  }
};

/**
 * Runs `task` over `items` with at most `concurrency` in flight. `task` should
 * handle its own errors; a throw ends that worker and rejects the whole run.
 */
export const runWithConcurrency = async <T>(
  items: readonly T[],
  concurrency: number,
  task: (item: T) => Promise<void>,
): Promise<void> => {
  const queue = [...items];
  const worker = async () => {
    for (let item = queue.shift(); item !== undefined; item = queue.shift()) {
      await task(item);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, queue.length) }, worker),
  );
};
